import { bareDomainOf } from "@/lib/company-mention-reliability";
import {
  COMPANY_TAG_BRIEF_MAX_LENGTH,
  COMPANY_TAG_LIMITS,
  COMPANY_TAG_MAX_SOURCES,
  COMPANY_TAG_SOURCE_URL_MAX_LENGTH,
  type CompanyTag,
} from "@shaarp/common";
import * as z from "zod/v3";

/**
 * The runtime schema of `CompanyTag` (`@shaarp/common`), UI side, on the bounds
 * `@shaarp/common` holds. Mirrors `api/src/common/utils/company-tag.ts`, whose
 * spec runs the same cases as `company-tag.test.ts` and pins the API's copy of
 * the bounds to the shared ones: the API cannot load runtime values from
 * `@shaarp/common`, so it owns a copy and the two are changed together.
 */

const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value);

/** A tag as it is shown: strict about what it holds, see `toCompanyTag`. */
export const companyTagSchema = z.object({
  id: z.string().max(COMPANY_TAG_LIMITS.id).optional(),
  name: z.string().min(1).max(COMPANY_TAG_LIMITS.name),
  city: z.string().max(COMPANY_TAG_LIMITS.city).optional(),
  address: z.string().max(COMPANY_TAG_LIMITS.address).optional(),
  siret: z.string().max(COMPANY_TAG_LIMITS.siret).optional(),
  siren: z.string().max(COMPANY_TAG_LIMITS.siren).optional(),
  domain: z.string().max(COMPANY_TAG_LIMITS.domain).optional(),
  linkedin: z.string().max(COMPANY_TAG_LIMITS.linkedin).optional(),
  activity: z.string().max(COMPANY_TAG_LIMITS.activity).optional(),
  source: z
    .array(
      z
        .string()
        .url()
        .max(COMPANY_TAG_SOURCE_URL_MAX_LENGTH)
        .refine(isHttpUrl, "Only http(s) pages are sources"),
    )
    .max(COMPANY_TAG_MAX_SOURCES)
    .optional(),
  brief: z.string().max(COMPANY_TAG_BRIEF_MAX_LENGTH).optional(),
});

/** What a marker or a set of props says, before it is a tag. */
export type CompanyTagFields = {
  readonly [Key in keyof CompanyTag]?: Key extends "source"
    ? string | readonly string[] | undefined
    : string | undefined;
};

/** An http(s) URL inside a `source` value: see {@link sourcesOf}. */
const SOURCE_URL_PATTERN = /https?:\/\/[^\s,|;"'<>[\]]+/gi;

/** Punctuation a sentence leaves at the end of a URL, which is not part of it. */
const URL_TRAILING_PUNCTUATION = /[.)\]]+$/;

/** A free-text value with its whitespace runs collapsed, or undefined when empty. */
function cleanText(value: string | undefined): string | undefined {
  const cleaned = value?.replace(/\s+/g, " ").trim();
  return cleaned ? cleaned : undefined;
}

/** {@link cleanText}, cut to `maxLength`. */
function cut(value: string | undefined, maxLength: number): string | undefined {
  return cleanText(value)?.slice(0, maxLength).trimEnd() || undefined;
}

/**
 * The http(s) URLs of a `source` value, whatever separates them (space, comma,
 * pipe), each once and at most `COMPANY_TAG_MAX_SOURCES`. Anything that is not
 * an http(s) URL is dropped: the value comes from a model that read the open web.
 * A square bracket ends a URL, as it ends the marker the value was read from.
 */
function sourcesOf(value: string | undefined): string[] | undefined {
  if (!value) return undefined;
  const urls = (value.match(SOURCE_URL_PATTERN) ?? []).map((url) =>
    url.replace(URL_TRAILING_PUNCTUATION, ""),
  );
  const distinct = [...new Set(urls)].slice(0, COMPANY_TAG_MAX_SOURCES);
  return distinct.length > 0 ? distinct : undefined;
}

/**
 * Reads a tag out of loosely written fields: whitespace collapsed, a domain
 * reduced to its host, `source` kept to the first http(s) pages, `brief` and
 * every other text cut to its limit. A value too long is shortened rather than
 * refused, so a chatty model still gets a clickable company.
 *
 * Null when there is no name to show — the one thing a tag cannot do without.
 */
export function toCompanyTag(fields: CompanyTagFields): CompanyTag | null {
  const candidate = {
    id: cut(fields.id, COMPANY_TAG_LIMITS.id),
    name: cut(fields.name, COMPANY_TAG_LIMITS.name),
    city: cut(fields.city, COMPANY_TAG_LIMITS.city),
    address: cut(fields.address, COMPANY_TAG_LIMITS.address),
    siret: cut(fields.siret, COMPANY_TAG_LIMITS.siret),
    siren: cut(fields.siren, COMPANY_TAG_LIMITS.siren),
    domain: bareDomainOf(fields.domain)?.slice(0, COMPANY_TAG_LIMITS.domain),
    linkedin: cut(fields.linkedin, COMPANY_TAG_LIMITS.linkedin),
    activity: cut(fields.activity, COMPANY_TAG_LIMITS.activity),
    source: sourcesOf(
      typeof fields.source === "string"
        ? fields.source
        : fields.source?.join(" "),
    ),
    brief: cut(fields.brief, COMPANY_TAG_BRIEF_MAX_LENGTH),
  };

  const defined = Object.fromEntries(
    Object.entries(candidate).filter(([, value]) => value !== undefined),
  );
  const parsed = companyTagSchema.safeParse(defined);
  return parsed.success ? parsed.data : null;
}

/**
 * The tag as it is sent to the API: without its `brief`, which is private to
 * the conversation and never leaves the browser for a lookup or a save
 * (`docs/decisions/0016`).
 */
export function withoutBrief(tag: CompanyTag): Omit<CompanyTag, "brief"> {
  const { brief: _brief, ...sent } = tag;
  return sent;
}

/** Whether a tag says enough about the company for a hover card to be worth showing. */
export function hasTagHoverContent(tag: CompanyTag): boolean {
  return Boolean(tag.city || tag.activity || tag.brief);
}
